import React, { useEffect, useState } from 'react';
import { Button, Tooltip } from 'antd';
import type { ButtonProps } from 'antd/es/button';
import classNames from 'classnames';

import ComponentBlocker from '@src/components/ComponentBlocker';
import CircleButton from '@src/components/Buttons/CircleButton';
import {
  getFilterShortcutAriaKeys,
  getFilterShortcutMessageId,
  isMacPlatform,
} from '@src/components/SecondaryPanel/utils/filterShortcut';
import { useIsMobile } from '@src/packages/utils';
import { generateKey } from '@src/packages/utils/functions';
import IntlMessages from '@src/util/IntlMessages';
import { LeftOutlined } from '@ant-design/icons';

import styles from './styles.module.less';
import {
  ButtonType,
  FilterContainerActionButton,
  FilterContainerProps,
} from './FilterContainer.interfaces';

const getCircleButtonTypeFromAction = (
  bt: Pick<FilterContainerActionButton, 'danger' | 'type'>,
): ButtonType => {
  if (bt?.danger) return 'danger';
  const t = bt?.type as string | undefined;
  if (t === 'primary') return 'primary';
  if (t === 'secondary') return 'secondary';
  if (t === 'dashed') return 'secondary';
  if (t === 'link' || t === 'text') return 'soft';
  return 'default';
};

const FILTER_TOGGLE_ICON = 'filter_alt';

const FilterContainer: React.FC<FilterContainerProps> = ({
  actionButtons = [],
  title = '',
  goBack = null,
  goBackIcon = <LeftOutlined className="gx-text-black" />,
  content = '',
  showHide = false,
  description = '',
  buttonItems = [],
  className = '',
  titleClassName = '',
  compactHeader = false,
  extraActionContent = '',
  leadingActionContent = '',
}) => {
  const { isMobile } = useIsMobile();
  const [show, setShow] = useState(true);
  const showHideButtonLabel = show ? 'button.hide' : 'button.showFilters';
  const showHideClass = show ? styles.visible : styles.hidden;
  const hasActionButton =
    actionButtons?.length > 0 || extraActionContent || leadingActionContent;
  const contentClass = show ? classNames(styles.boxB, showHideClass) : showHideClass;

  const toggleShowHide = () => setShow(prev => !prev);

  const getSizeButton = (size: ButtonProps['size']) => {
    if (isMobile) return 'small';
    return size || 'middle';
  };

  useEffect(() => {
    if (isMobile) setShow(false);
    else setShow(true);
  }, [isMobile]);

  const hasHeaderButtons =
    buttonItems.length > 0 ||
    actionButtons.length > 0 ||
    Boolean(leadingActionContent) ||
    Boolean(extraActionContent);

  return (
    <div className={classNames(styles.container, compactHeader && styles.compactHeader, className)}>
      <div className={classNames(styles.body, !showHide && !description && 'gx-pb-2')}>
        <div className={styles.filterContainer}>
          <div className={classNames(styles.boxA, !hasActionButton && 'gx-guarnic-pb-1')}>
            <div className={styles.titleContainer}>
              {goBack && (
                <div className={styles.leftIcon}>
                  <Button
                    data-testid="filter-container-back-button"
                    className="gx-mb-0"
                    onClick={goBack}
                    shape="circle"
                    icon={goBackIcon}
                    size={compactHeader ? 'small' : 'middle'}
                  />
                </div>
              )}
              {title && (
                <div className={classNames(styles.titleChildren, titleClassName)}>{title}</div>
              )}
            </div>
            {hasHeaderButtons && (
              <div className={classNames(styles.buttonsContainer, 'gx-d-flex', 'gx-guarnic-gap-1')}>
                {leadingActionContent ? <>{leadingActionContent}</> : null}
                {actionButtons.map((bt, idx) => {
                  if (bt?.hidden) return null;

                  const useCircleOnMobile = isMobile && bt?.iconName;
                  const showShortcutHint = bt?.iconName === FILTER_TOGGLE_ICON && !isMobile;
                  const isMac = showShortcutHint && isMacPlatform();

                  const circleButton = (
                    <CircleButton
                      iconName={bt.iconName}
                      onClick={bt?.action}
                      disabled={bt?.disabled || false}
                      hidden={bt?.hidden || false}
                      loading={bt?.loading || false}
                      label=""
                      tooltipText={(bt?.tooltipText ?? bt?.label) as string | undefined}
                      tooltipPosition={bt?.tooltipPosition ?? 'left'}
                      type={getCircleButtonTypeFromAction(bt)}
                      id={bt?.props?.id}
                    />
                  );

                  const antButton = (
                    <Button
                      className={classNames(styles.actionButton, bt?.className)}
                      {...bt?.props}
                      size={getSizeButton(bt?.size)}
                      type={bt?.type as ButtonProps['type']}
                      danger={bt?.danger || false}
                      onClick={bt?.action}
                      disabled={bt?.disabled || false}
                      loading={bt?.loading || false}
                      hidden={bt?.hidden || false}
                      htmlType={bt?.htmlType}
                      aria-keyshortcuts={
                        showShortcutHint ? getFilterShortcutAriaKeys(isMac) : undefined
                      }
                    >
                      {bt?.iconName && (
                        <i className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                          {bt?.iconName}
                        </i>
                      )}
                      {bt?.label}
                      {showShortcutHint && (
                        <span className={styles.shortcutHint} aria-hidden="true">
                          <IntlMessages id={getFilterShortcutMessageId(isMac)} />
                        </span>
                      )}
                    </Button>
                  );

                  const desktopButton = bt?.tooltipText ? (
                    <Tooltip title={bt.tooltipText}>
                      <span>{antButton}</span>
                    </Tooltip>
                  ) : antButton;
                  const actionContent = useCircleOnMobile ? circleButton : desktopButton;

                  return (
                    <React.Fragment
                      key={bt.key ?? bt.props?.id ?? `${bt.iconName || 'action'}-${idx}`}
                    >
                      {bt?.allowedRole && bt?.allowedRole.length > 0 ? (
                        <ComponentBlocker allowedRoles={bt.allowedRole} divId={bt?.divId}>
                          {actionContent}
                        </ComponentBlocker>
                      ) : (
                        actionContent
                      )}
                    </React.Fragment>
                  );
                })}

                {hasActionButton && extraActionContent && <>{extraActionContent}</>}

                {buttonItems?.map((bt, idx) => {
                  if (bt?.hidden) return null;

                  const handleButtonClick = () => {
                    if (bt.action) {
                      bt.action({ toggleShowHide, show });
                    }
                  };

                  return (
                    <React.Fragment key={generateKey(`item-${idx}`)}>
                      {bt?.allowedRole && bt?.allowedRole.length > 0 ? (
                        <ComponentBlocker allowedRoles={bt.allowedRole} divId={bt?.divId}>
                          <CircleButton
                            iconName={bt?.iconName}
                            onClick={handleButtonClick}
                            disabled={bt?.disabled || false}
                            hidden={bt?.hidden || false}
                            label={bt?.label}
                            tooltipText={bt?.tooltipText}
                            tooltipPosition={bt?.tooltipPosition}
                            type={bt?.type}
                          />
                        </ComponentBlocker>
                      ) : (
                        <CircleButton
                          iconName={bt?.iconName}
                          onClick={handleButtonClick}
                          disabled={bt?.disabled || false}
                          hidden={bt?.hidden || false}
                          label={bt?.label}
                          tooltipText={bt?.tooltipText}
                          tooltipPosition={bt?.tooltipPosition}
                          type={bt?.type}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </div>
          {content && <div className={contentClass}>{content}</div>}
          {(showHide || description) && (
            <div className={styles.boxC}>
              {description ? <div>{description}</div> : <div />}
              {showHide && (
                <Button className="gx-mb-0 gx-pr-0" onClick={toggleShowHide} type="link">
                  <IntlMessages id={showHideButtonLabel} />
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilterContainer;
